import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
} from "draft-js";
// import uuid from "react-native-uuid";

// Internal Imports
import { DOCUMENTS, DRAFT, ERROR_MSG } from "../constants";
import { DOCUMENT_BLOCKS } from "../components/DocumentV2/constants";

// Internal Imports
import { normaliseTableData } from "./tableUtils";
import { getEditorStateForClauseOptions } from "./draftUtils";
import HTMLtoDraft from "./HTMLtoDraft/HTMLtoDraft";
import getCompositeDecorator from "../components/CustomRevvEditor/decorator";

// Destructuring
const { COMPONENT_FEATURE, DOCUMENT_COMPONENT_TYPES, DOCUMENT_MODE } =
  DOCUMENTS;
const { API_ERRORS } = ERROR_MSG;

export const getComponentData = (components, componentMeta) => {
  // if (!(componentMeta || components)) return null;
  if (!componentMeta || !components) {
    return null;
  }

  const { componentId, blockType } = componentMeta;
  let component;
  if (blockType === DOCUMENT_BLOCKS.BODY) {
    component = components[componentId];
  } else if (
    blockType === DOCUMENT_BLOCKS.COVERPAGE &&
    componentMeta.coverpageId
  ) {
    const { coverpageId } = componentMeta;
    component =
      components[coverpageId] &&
      components[coverpageId].content &&
      components[coverpageId].content.data
        ? components[coverpageId].content.data.components[componentId]
        : null;
  } else if (blockType === DOCUMENT_BLOCKS.HEADER && componentMeta.headerId) {
    const { headerId } = componentMeta;
    component =
      components[headerId] &&
      components[headerId].content &&
      components[headerId].content.data &&
      components[headerId].content.data.header
        ? components[headerId].content.data.header.components[componentId]
        : null;
  } else if (blockType === DOCUMENT_BLOCKS.FOOTER && componentMeta.footerId) {
    const { footerId } = componentMeta;
    component =
      components[footerId] &&
      components[footerId].content &&
      components[footerId].content.data &&
      components[footerId].content.data.footer
        ? components[footerId].content.data.footer.components[componentId]
        : null;
  }

  if (!component) return null;

  return component;
};

export const getComponentEditorDataFromSection = (section, componentMeta) => {
  if (!(section && componentMeta)) return null;
  const { componentType } = componentMeta;
  if (componentType === DOCUMENT_COMPONENT_TYPES.HTML) {
    return section;
  }
  if (componentType === DOCUMENT_COMPONENT_TYPES.TABLE) {
    const { data: tableData } = section;
    const { tableRowId, tableColId } = componentMeta;
    if (
      tableData &&
      tableData.data[tableRowId] &&
      tableData.data[tableRowId].data[tableColId]
    ) {
      const {
        attributes: { id },
        data,
      } = tableData.data[tableRowId].data[tableColId];
      return { ...data, id };
    }
    return null;
  }
  return null;
};

export const getComponentEditorData = (components, componentMeta) => {
  // const component = getComponentFromInfo(components, componentMeta);
  const component = getComponentData(components, componentMeta);
  return component && component.content
    ? getComponentEditorDataFromSection(component.content, componentMeta)
    : null;
};

export const isDraftRawDataObj = data => {
  if (
    data &&
    typeof data === "object" &&
    DRAFT.DRAFT_RAW_KEYS.length === Object.keys(data).length &&
    JSON.stringify(DRAFT.DRAFT_RAW_KEYS.sort()) ===
      JSON.stringify(Object.keys(data).sort())
  ) {
    return true;
  }
  return false;
};

const checkInlineStylingStructure = data => {
  const { blocks } = data;
  blocks.forEach(block => {
    const { inlineStyleRanges } = block;
    const updatedinlineStyleRanges = [];
    inlineStyleRanges.forEach(styleItem => {
      if (styleItem.style instanceof Array) {
        styleItem.style.forEach(sty => {
          updatedinlineStyleRanges.push({ ...styleItem, style: sty });
        });
      }
    });
    if (updatedinlineStyleRanges.length > 0) {
      block.inlineStyleRanges = updatedinlineStyleRanges;
    }
  });
  return data;
};

export const convertRawToDraft = data => {
  try {
    const updatedData = checkInlineStylingStructure(data);
    if (isDraftRawDataObj(updatedData)) {
      return convertFromRaw(updatedData);
    }
    console.error(`Raw to draft conversion error. Raw keys unavailable`);
  } catch (error) {
    console.error(
      `Unable to convert the html object into editor object. Error: ${error.message}`,
    );
    throw Error(API_ERRORS.DEFAULT_MSG);
  }
  const editorState = EditorState.createEmpty();
  return editorState.getCurrentContent();
};

export const convertHTMLStringToDraft = (html, mode) => {
  try {
    return HTMLtoDraft(html, mode);
  } catch (error) {
    console.error(
      `Unable to convert the html object into editor object. Error: ${error.message}`,
    );
    throw Error(API_ERRORS.DEFAULT_MSG);
  }
};

const _hasCPValue = clauseOptionsDetails => {
  let isFilledCase = false;
  const { value, meta: { subtype } = {} } = clauseOptionsDetails;

  if (value) {
    if (subtype === "free_text") {
      if (typeof value === "object") {
        if (value.value) {
          isFilledCase = true;
        }
      } else {
        isFilledCase = true;
      }
    } else if (typeof value === "string") {
      isFilledCase = true;
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        isFilledCase = true;
      } else {
        isFilledCase = false;
      }
    } else if (typeof value === "object") {
      isFilledCase = true;
    }
  }
  return isFilledCase;
};

export const getClauseOptionEditorState = (
  section,
  mode,
  formFields,
  isSharedDocument,
) => {
  let editorState = null;
  if (section.clauseOptions && formFields && formFields.length > 0) {
    const clauseOptionsDetails = formFields.find(
      clause => clause.id === section.clauseOptions.id,
    );

    if (clauseOptionsDetails) {
      if (
        isSharedDocument &&
        (mode === "TEMPLATE_DOCUMENT" || mode === "DOCUMENT") &&
        !_hasCPValue(clauseOptionsDetails) &&
        (window.location.pathname.includes("/perma/DOC") ||
          window.location.pathname.includes("/m/"))
      ) {
        editorState = EditorState.createEmpty(getCompositeDecorator());
      } else {
        editorState = getEditorStateForClauseOptions(
          section.clauseOptions.value,
          clauseOptionsDetails,
          mode,
        );
      }
    }
  }
  return editorState || EditorState.createEmpty(getCompositeDecorator());
};

export const updateSectionDataForEntity = (
  data = {},
  anchorKey,
  anchorOffset,
  focusKey,
  focusOffset,
  length,
  entityData,
) => {
  const { blocks = [] } = data;
  let tempBlocks = [];

  // Single line selection case
  if (anchorKey === focusKey) {
    tempBlocks = blocks.filter(block => block.key === anchorKey);
  }
  // Multi lines selection case
  else {
    let startIndex = blocks.findIndex(block => block.key === anchorKey);
    let endIndex = blocks.findIndex(block => block.key === focusKey);
    if (startIndex > endIndex) {
      let tempVar = 0;
      tempVar = startIndex;
      startIndex = endIndex;
      endIndex = tempVar;
    }
    tempBlocks = blocks.slice(startIndex, endIndex + 1);
  }

  // Update block's entityRanges
  tempBlocks.forEach(block => {
    let blockEntityRanges = [];
    let calcLength = length;
    let calcOffset = anchorOffset;
    if (anchorKey !== focusKey) {
      if (block.key === anchorKey) {
        calcLength = block.text.length - anchorOffset;
        calcOffset = anchorOffset;
      } else if (block.key === focusKey) {
        calcLength = focusOffset + 1;
        calcOffset = 0;
      } else {
        calcLength = block.text.length;
        calcOffset = 0;
      }
    }
    blockEntityRanges = [
      ...block.entityRanges,
      {
        key: Object.keys(data.entityMap).length,
        length: calcLength,
        offset: calcOffset,
      },
    ];
    block.entityRanges = blockEntityRanges;
  });

  // Update entity data
  data.entityMap[Object.keys(data.entityMap).length] = {
    data: { ...entityData },
    mutability: "MUTABLE",
    type: "NOTES",
  };

  return data;
};

export const convertHTMLRawToDraft = (section, mode) => {
  // check if it is just a string
  if (typeof section === "string") {
    return section;
  }

  const draftData = {};
  const { data, currentContent } = section;
  let contentState;

  if (typeof data === "string") {
    const htmlContent = convertHTMLStringToDraft(section.data, mode);
    contentState =
      htmlContent instanceof ContentState ? htmlContent : undefined;
  } else if (typeof data === "object") {
    const rawData =
      COMPONENT_FEATURE[mode].allowDiff && currentContent
        ? currentContent
        : data;

    if (isDraftRawDataObj(rawData)) {
      contentState = convertRawToDraft(rawData);
    } else if (rawData instanceof ContentState) {
      contentState = rawData;
    }
  }

  draftData.data = contentState;
  return draftData;
};

export const convertTableRawtoDraft = (sectionData, mode) => {
  const hiddenCols =
    sectionData.attributes && Array.isArray(sectionData.attributes.hiddenCols)
      ? sectionData.attributes.hiddenCols
      : [];
  const tableData = normaliseTableData(
    sectionData,
    DOCUMENT_COMPONENT_TYPES.TABLE,
    hiddenCols,
  );
  // if (Array.isArray(tableData.data)) {
  //   const draftTableData = tableData.data.map(row => ({
  //     ...row,
  //     data: row.data.map(cell => {
  //       if (
  //         cell.data.data &&
  //         cell.data.data instanceof ContentState
  //         // cell.data.editorState instanceof EditorState
  //       ) {
  //         return cell;
  //       }
  //       const cellData = {
  //         ...cell,
  //         // data: convertHTMLRawToDraft(cell.data, mode),
  //       };
  //       if (cell.data.html) cellData.data.html = cell.data.html;
  //       return cellData;
  //     }),
  //   }));
  //   tableData = {
  //     ...tableData,
  //     data: draftTableData,
  //   };
  // }
  if (mode === DOCUMENT_MODE.TEMPLATE) {
    tableData.attributes.showDataSourceOption = false;
  }
  return tableData;
};

export const convertSectionRawToDraft = (section, mode) => {
  const modifiedSection = {
    id: section.section_id || section.id,
    type: section.section_type || section.type,
    lock: section.lock || false,
    label: section.section_name || section.label,
    header: section.header,
    data: section.data,
    clauseID: section.clauseID || section.clause,
    clauseOptions: section.clauseOptions || null,
  };

  if (
    modifiedSection.type === DOCUMENT_COMPONENT_TYPES.HTML &&
    section.currentContent &&
    (mode === DOCUMENT_MODE.DOCUMENT ||
      mode === DOCUMENT_MODE.TEMPLATE_DOCUMENT)
  ) {
    modifiedSection.data = section.currentContent;
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE) {
    modifiedSection.showGroupedData = section.showGroupedData;
    modifiedSection.showUngroupedData = section.showUngroupedData;
    modifiedSection.dealtableView = section.dealtableView;
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    modifiedSection.data = convertTableRawtoDraft(section.data, mode);
    const {
      data: {
        attributes: { isLinkedToDataSource },
      },
    } = modifiedSection;
    if (isLinkedToDataSource) {
      modifiedSection.sheet = section.sheet;
      modifiedSection.workSheet = section.workSheet;
      modifiedSection.rangeValue = section.rangeValue;
      modifiedSection.autoTrim = section.autoTrim;
      modifiedSection.connected = section.connected;
    }
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
    modifiedSection.sheet = section.sheet;
    modifiedSection.workSheet = section.workSheet;
    modifiedSection.rangeValue = section.rangeValue;
    modifiedSection.autoTrim = section.autoTrim;
    modifiedSection.connected = section.connected;
    modifiedSection.data = convertTableRawtoDraft(section.data, mode);
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    const headerComponents = {};
    const footerComponents = {};
    Object.keys(section.data.footer.components).forEach(componentId => {
      footerComponents[componentId] = {
        ...section.data.footer.components[componentId],
        content: convertSectionRawToDraft(
          section.data.footer.components[componentId].content,
          mode,
        ),
      };
    });
    Object.keys(section.data.header.components).forEach(componentId => {
      headerComponents[componentId] = {
        ...section.data.header.components[componentId],
        content: convertSectionRawToDraft(
          section.data.header.components[componentId].content,
          mode,
        ),
      };
    });
    modifiedSection.data = {
      footer: {
        ...section.data.footer,
        components: footerComponents,
      },
      header: {
        ...section.data.header,
        components: headerComponents,
      },
    };
  } else if (modifiedSection.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    const newComponents = {};
    Object.keys(section.data.components).forEach(componentId => {
      newComponents[componentId] = {
        ...section.data.components[componentId],
        content: convertSectionRawToDraft(
          section.data.components[componentId].content,
          mode,
        ),
      };
    });
    modifiedSection.data = {
      ...section.data,
      components: newComponents,
    };
  } else if (
    modifiedSection.type === DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER
  ) {
    modifiedSection.justify_content = section.justify_content || "";
  }
  return modifiedSection;
};

export const getComponentMetaFromEntity = (components, entity) => {
  const { sectionID, id: entityID } = entity;

  let section;
  const componentMeta = {
    componentId: sectionID,
  };
  if (components[sectionID] === undefined) {
    // check if it is in coverpage or header footer
    const coverpageId = Object.keys(components).find(
      componentId =>
        components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.COVERPAGE,
    );

    const headerFooterId = Object.keys(components).find(
      componentId =>
        components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER,
    );

    if (
      coverpageId &&
      components[coverpageId].content.data.components[sectionID] !== undefined
    ) {
      componentMeta.blockType = DOCUMENT_BLOCKS.COVERPAGE;
      componentMeta.coverpageId = coverpageId;
      section =
        components[coverpageId].content.data.components[sectionID].content;
    } else if (
      headerFooterId &&
      components[headerFooterId].content.data.header.components[sectionID] !==
        undefined
    ) {
      componentMeta.blockType = DOCUMENT_BLOCKS.HEADER;
      componentMeta.headerId = headerFooterId;
      section =
        components[headerFooterId].content.data.header.components[sectionID]
          .content;
    } else if (
      headerFooterId &&
      components[headerFooterId].content.data.footer.components[sectionID] !==
        undefined
    ) {
      componentMeta.blockType = DOCUMENT_BLOCKS.FOOTER;
      componentMeta.footerId = headerFooterId;
      section =
        components[headerFooterId].content.data.footer.components[sectionID]
          .content;
    }
  } else {
    componentMeta.blockType = DOCUMENT_BLOCKS.BODY;
    section = components[sectionID].content;
  }

  componentMeta.componentType = section.type;

  if (section.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    return componentMeta;
  }
  if (section.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    const { data: tableData } = section.data;
    let rowID;
    let columnID;
    let tableCellId;
    for (let rowIndex = 0; rowIndex < tableData.length; rowIndex += 1) {
      const row = tableData[rowIndex];
      const { data: rowData } = row;
      for (let colIndex = 0; colIndex < rowData.length; colIndex += 1) {
        const {
          data: cellData,
          attributes: { id },
        } = rowData[colIndex];
        if (cellData && cellData.data && typeof cellData.data === "object") {
          let rawData = cellData.data;
          if (rawData instanceof ContentState) {
            rawData = convertToRaw(cellData.data);
          }
          const { entityMap } = rawData;
          const findEntity = Object.keys(entityMap).find(entityKey => {
            const entityData = entityMap[entityKey];
            return entityData.data.id === entityID;
          });
          if (findEntity) {
            rowID = rowIndex;
            columnID = colIndex;
            tableCellId = id;
            break;
          }
        }
      }
    }
    componentMeta.tableId = sectionID;
    componentMeta.tableColId = columnID;
    componentMeta.tableRowId = rowID;
    componentMeta.tableCellId = tableCellId;
  }
  return componentMeta;
};

export const applyNotesInRawSectionData = (data, sectionId, comments) => {
  if (Array.isArray(comments)) {
    const sectionData = { ...data };
    comments.forEach(item => {
      const { noteMappingConfigs } = item.anchor;
      if (
        noteMappingConfigs &&
        (noteMappingConfigs.componentId === sectionId ||
          noteMappingConfigs.tableCellId === sectionId)
      ) {
        const { anchorKey, focusKey } = noteMappingConfigs.selectionData;
        const { blocks } = sectionData;

        let tempBlocks = [];
        // If single line selection
        if (anchorKey === focusKey) {
          tempBlocks = blocks.filter(block => block.key === anchorKey);
        }
        // If multi lines selection
        else {
          let startIndex = blocks.findIndex(block => block.key === anchorKey);
          let endIndex = blocks.findIndex(block => block.key === focusKey);
          if (startIndex > endIndex) {
            let tempVar = 0;
            tempVar = startIndex;
            startIndex = endIndex;
            endIndex = tempVar;
          }
          tempBlocks = blocks.slice(startIndex, endIndex + 1);
        }

        // Updating the block's "entityRanges"
        tempBlocks.forEach(block => {
          let blockEntityRanges = [];
          blockEntityRanges = [
            ...block.entityRanges,
            {
              key: Object.keys(sectionData.entityMap).length,
              length: noteMappingConfigs.selectionData.length,
              offset: noteMappingConfigs.selectionData.acnhorOffset,
            },
          ];
          block.entityRanges = blockEntityRanges;
        });

        // Updating the entity map
        sectionData.entityMap[Object.keys(sectionData.entityMap).length] = {
          data: { id: item.anchor.id },
          mutability: "MUTABLE",
          type: "NOTES",
        };
      }
    });
    return sectionData;
  }
  return data;
};

export const getContentStateFromRawSectionData = (
  section,
  mode = "DOCUMENT",
  componentMeta,
  comments,
  formFieldList,
  isSharedDocument,
) => {
  let contentState;
  // for clause options
  if (section.clauseOptions && section.type === "HTML") {
    const claseOptionEditorState = getClauseOptionEditorState(
      section,
      mode,
      formFieldList,
      isSharedDocument,
    );
    contentState = claseOptionEditorState.getCurrentContent();
  } else {
    const { data, currentContent } = section;

    // 1.  Docx uploaded or old table data case
    if (typeof data === "string") {
      const htmlContent = convertHTMLStringToDraft(data, mode);
      contentState =
        htmlContent instanceof ContentState ? htmlContent : undefined;
    }
    // 2.  Raw format data case
    else if (typeof data === "object") {
      let rawData =
        COMPONENT_FEATURE[mode].allowDiff && currentContent
          ? currentContent
          : data;
      if (isDraftRawDataObj(rawData)) {
        if (componentMeta && comments) {
          const sectionId = componentMeta
            ? componentMeta.tableCellId || componentMeta.componentId
            : section.id || section.section_id;
          rawData = applyNotesInRawSectionData(
            COMPONENT_FEATURE[mode].allowDiff && currentContent
              ? currentContent
              : data,
            sectionId,
            comments,
          );
        }
        contentState = convertRawToDraft(rawData);
      }
      // 3.  Content state format case
      else if (rawData instanceof ContentState) {
        contentState = rawData;
      }
    }
  }
  return contentState;
};
